import { trackEvent } from '@/lib/analytics';

interface AppStoreBadgesProps {
  variant?: 'dark' | 'light';
}

export function AppStoreBadges({ variant = 'dark' }: AppStoreBadgesProps) {
  const handleClick = (platform: string) => {
    trackEvent('app_badge_click', { platform });
  };

  const isDark = variant === 'dark';
  const bgColor = isDark ? '#000000' : '#FFFFFF';
  const textColor = isDark ? '#FFFFFF' : '#000000';
  const borderColor = isDark ? 'none' : '#D1D5DB';

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
      {/* Official Apple App Store Badge */}
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          handleClick('ios');
        }}
        className="transition-transform hover:scale-105 active:scale-95"
        aria-label="Download on the App Store"
      >
        <svg
          width="180"
          height="60"
          viewBox="0 0 180 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="drop-shadow-lg"
        >
          {/* Background */}
          <rect width="180" height="60" rx="8" fill={bgColor} />
          {!isDark && <rect width="180" height="60" rx="8" stroke={borderColor} strokeWidth="1.5" fill="none" />}
          
          {/* Apple Logo */}
          <g transform="translate(18, 15)">
            <path
              d="M24.769 12.856c-.043 4.378 3.525 6.495 3.686 6.585-.031.102-.576 2.052-1.899 4.066-1.144 1.741-2.331 3.478-4.201 3.514-1.836.036-2.426-.993-4.525-.993-2.1 0-2.757.958-4.492.993-1.803.036-3.184-1.908-4.338-3.642-2.358-3.543-4.159-10.009-1.74-14.367 1.202-2.169 3.351-3.542 5.681-3.578 1.772-.036 3.445 1.192 4.527 1.192 1.08 0 3.107-1.474 5.233-1.257.891.037 3.394.36 5.001 2.713-.129.08-2.984 1.743-2.933 5.774M19.38 4.285c.957-1.158 1.602-2.768 1.426-4.369-1.378.056-3.046.918-4.034 2.076-.886 1.027-1.662 2.668-1.452 4.245 1.536.12 3.106-.78 4.06-1.952"
              fill={textColor}
            />
          </g>
          
          {/* Download on the text */}
          <text x="55" y="24" fill={textColor} fontSize="10.5" fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif" fontWeight="400">
            Download on the
          </text>
          
          {/* App Store text */}
          <text x="55" y="42" fill={textColor} fontSize="22" fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif" fontWeight="600" letterSpacing="-0.5">
            App Store
          </text>
        </svg>
      </a>

      {/* Official Google Play Badge */}
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          handleClick('android');
        }}
        className="transition-transform hover:scale-105 active:scale-95"
        aria-label="Get it on Google Play"
      >
        <svg
          width="180"
          height="60"
          viewBox="0 0 180 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="drop-shadow-lg"
        >
          {/* Background */}
          <rect width="180" height="60" rx="8" fill={bgColor} />
          {!isDark && <rect width="180" height="60" rx="8" stroke={borderColor} strokeWidth="1.5" fill="none" />}
          
          {/* Google Play Icon - Scaled down and positioned better */}
          <g transform="translate(15, 10) scale(0.85)">
            <path d="M3.609 3.661C3.22 4.05 3 4.668 3 5.438v29.125c0 .77.22 1.387.609 1.777l.063.062 16.329-16.328v-.386L3.672 3.598 3.609 3.66z" fill="#32BBFF"/>
            <path d="M25.442 25.442l-5.442-5.442v-.386l5.442-5.442.078.043 6.442 3.662c1.84 1.043 1.84 2.751 0 3.795l-6.442 3.662-.078.043z" fill="#32BBFF"/>
            <path d="M25.52 25.399L20 19.879 3.609 36.27c.609.642 1.609.72 2.751.078l19.16-10.87" fill="#32BBFF"/>
            <path d="M25.52 14.36L6.36 3.49c-1.142-.642-2.142-.564-2.751.078L20 19.879l5.52-5.52z" fill="#32BBFF"/>
            
            {/* Gradient overlays for authentic look */}
            <defs>
              <linearGradient id="playGrad1" x1="21.8" y1="8.71" x2="5.02" y2="25.49" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#FFD900"/>
                <stop offset="1" stopColor="#FFB300"/>
              </linearGradient>
              <linearGradient id="playGrad2" x1="33.83" y1="20.25" x2="9.64" y2="20.25" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#00D7FF"/>
                <stop offset="1" stopColor="#00A3FF"/>
              </linearGradient>
              <linearGradient id="playGrad3" x1="24.83" y1="28.68" x2="2.07" y2="51.44" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#00F076"/>
                <stop offset="1" stopColor="#00C96B"/>
              </linearGradient>
              <linearGradient id="playGrad4" x1="7.3" y1="0.18" x2="17.46" y2="10.34" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#FF3E00"/>
                <stop offset="1" stopColor="#E50000"/>
              </linearGradient>
            </defs>
            
            <path d="M25.52 14.36L6.36 3.49c-1.142-.642-2.142-.564-2.751.078L20 19.879l5.52-5.52z" fill="url(#playGrad1)"/>
            <path d="M25.442 25.442l-5.442-5.442v-.386l5.442-5.442.078.043 6.442 3.662c1.84 1.043 1.84 2.751 0 3.795l-6.442 3.662-.078.043z" fill="url(#playGrad2)"/>
            <path d="M25.52 25.399L20 19.879 3.609 36.27c.609.642 1.609.72 2.751.078l19.16-10.87" fill="url(#playGrad3)"/>
            <path d="M3.609 3.661C3.22 4.05 3 4.668 3 5.438v29.125c0 .77.22 1.387.609 1.777l.063.062 16.329-16.328v-.386L3.672 3.598 3.609 3.66z" fill="url(#playGrad4)"/>
          </g>
          
          {/* GET IT ON text */}
          <text x="60" y="24" fill={textColor} fontSize="10.5" fontFamily="'Roboto', Arial, sans-serif" fontWeight="400" letterSpacing="0.5">
            GET IT ON
          </text>
          
          {/* Google Play text */}
          <text x="60" y="42" fill={textColor} fontSize="21" fontFamily="'Roboto', Arial, sans-serif" fontWeight="500">
            Google Play
          </text>
        </svg>
      </a>
    </div>
  );
}

